import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { 
  Settings, Globe, Mic, Bot, AudioLines, MessageCircle, 
  FileText, Info, Plus, Trash2, GripVertical, Play, Pause, Volume2, Loader2, Square
} from 'lucide-react';
import { voiceOptions, llmModels, transcriptionServices, languages } from '../../../constants/agentConfig';
import ScriptEnhancer from '../../ScriptEnhancer';
import { voiceAIAPI } from '../../../services/api';

// Audio Waveform Visualizer Component
const AudioWaveform = ({ isPlaying, audioElement }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const analyserRef = useRef(null);
  const audioContextRef = useRef(null);
  const sourceRef = useRef(null);

  useEffect(() => {
    if (!isPlaying || !audioElement) {
      // Stop animation when not playing
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      // Draw idle state
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        // Draw idle bars
        const barCount = 20;
        const barWidth = (canvas.width / barCount) - 2;
        ctx.fillStyle = '#374151';
        for (let i = 0; i < barCount; i++) {
          const x = i * (barWidth + 2);
          const height = 4;
          const y = (canvas.height - height) / 2;
          ctx.fillRect(x, y, barWidth, height);
        }
      }
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    
    // Initialize Audio Context and Analyser
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    
    const audioContext = audioContextRef.current;
    
    if (!analyserRef.current) {
      analyserRef.current = audioContext.createAnalyser();
      analyserRef.current.fftSize = 64;
    }
    
    const analyser = analyserRef.current;
    
    // Connect audio element to analyser (only once per element)
    if (!sourceRef.current && audioElement) {
      try {
        sourceRef.current = audioContext.createMediaElementSource(audioElement);
        sourceRef.current.connect(analyser);
        analyser.connect(audioContext.destination);
      } catch (e) {
        // Element might already be connected, continue with animation
        console.log('Audio source already connected');
      }
    }

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      if (!isPlaying) {
        cancelAnimationFrame(animationRef.current);
        return;
      }

      animationRef.current = requestAnimationFrame(draw);
      analyser.getByteFrequencyData(dataArray);

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const barCount = 20;
      const barWidth = (canvas.width / barCount) - 2;
      const maxHeight = canvas.height - 4;

      for (let i = 0; i < barCount; i++) {
        // Map data to bars
        const dataIndex = Math.floor(i * (bufferLength / barCount));
        const value = dataArray[dataIndex] || 0;
        const barHeight = Math.max(4, (value / 255) * maxHeight);
        
        const x = i * (barWidth + 2);
        const y = (canvas.height - barHeight) / 2;

        // Gradient color from teal to cyan
        const gradient = ctx.createLinearGradient(x, y, x, y + barHeight);
        gradient.addColorStop(0, '#2dd4bf');
        gradient.addColorStop(0.5, '#14b8a6');
        gradient.addColorStop(1, '#0d9488');
        
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.roundRect(x, y, barWidth, barHeight, 2);
        ctx.fill();
      }
    };

    // Resume audio context if suspended
    if (audioContext.state === 'suspended') {
      audioContext.resume();
    }
    
    draw();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying, audioElement]);

  return (
    <canvas 
      ref={canvasRef} 
      width={160} 
      height={32}
      className="rounded"
    />
  );
};

// Voice preview component
const VoicePreviewButton = ({ voice, provider, isPlaying, onPlay, onStop, isLoading }) => {
  return (
    <button
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (isPlaying) {
          onStop();
        } else {
          onPlay(voice, provider);
        }
      }}
      disabled={isLoading}
      className={`p-1.5 rounded-full transition-colors ${
        isPlaying 
          ? 'bg-teal-500 text-white' 
          : 'bg-gray-700 text-gray-400 hover:bg-gray-600 hover:text-white'
      }`}
      title={isPlaying ? 'Stop preview' : `Preview ${voice.name}'s voice`}
    >
      {isLoading ? (
        <Loader2 className="w-3 h-3 animate-spin" />
      ) : isPlaying ? (
        <Square className="w-3 h-3" />
      ) : (
        <Play className="w-3 h-3" />
      )}
    </button>
  );
};

const DetailsTab = ({ 
  agent, 
  setAgent, 
  hasFeature, 
  getVoiceSetting, 
  isVoiceAllowed,
  addFlowSection,
  updateFlowSection,
  deleteFlowSection
}) => {
  const selectedLLM = llmModels.find(m => m.id === agent.llmModel);
  const audioRef = useRef(null);
  const audioElementRef = useRef(null);
  const [playingVoiceId, setPlayingVoiceId] = useState(null);
  const [loadingVoiceId, setLoadingVoiceId] = useState(null);
  const [audioError, setAudioError] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);

  // Memoize expensive filter operations for LLM model groups
  const openaiModels = useMemo(() => llmModels.filter(m => m.provider === 'OpenAI'), []);
  const anthropicModels = useMemo(() => llmModels.filter(m => m.provider === 'Anthropic'), []);
  const googleModels = useMemo(() => llmModels.filter(m => m.provider === 'Google'), []);
  const metaModels = useMemo(() => llmModels.filter(m => m.provider.includes('Meta')), []);
  const mistralModels = useMemo(() => llmModels.filter(m => m.provider === 'Mistral'), []);

  // Language-specific sample texts for voice preview
  const sampleTexts = {
    'English': "Hello! I'm your AI assistant. How can I help you today?",
    'Russian': "Здравствуйте! Я ваш ИИ-ассистент. Чем могу помочь?",
    'Uzbek': "Salom! Men sizning AI yordamchingizman. Sizga qanday yordam bera olaman?",
    'Spanish': "¡Hola! Soy tu asistente de IA. ¿En qué puedo ayudarte hoy?",
    'French': "Bonjour! Je suis votre assistant IA. Comment puis-je vous aider?",
    'German': "Hallo! Ich bin Ihr KI-Assistent. Wie kann ich Ihnen helfen?",
    'Chinese': "你好！我是您的AI助手。今天我能帮您什么？",
    'Japanese': "こんにちは！AIアシスタントです。今日はどのようにお手伝いできますか？",
    'Korean': "안녕하세요! AI 어시스턴트입니다. 무엇을 도와드릴까요?",
    'Arabic': "مرحباً! أنا مساعدك الذكي. كيف يمكنني مساعدتك اليوم؟",
    'Hindi': "नमस्ते! मैं आपका AI सहायक हूं। आज मैं आपकी कैसे मदद कर सकता हूं?",
    'Portuguese': "Olá! Sou seu assistente de IA. Como posso ajudá-lo hoje?",
    'Italian': "Ciao! Sono il tuo assistente IA. Come posso aiutarti oggi?",
    'Dutch': "Hallo! Ik ben uw AI-assistent. Hoe kan ik u vandaag helpen?",
    'Polish': "Cześć! Jestem twoim asystentem AI. Jak mogę ci dzisiaj pomóc?",
    'Turkish': "Merhaba! Ben yapay zeka asistanınızım. Size bugün nasıl yardımcı olabilirim?",
  };

  // Get appropriate sample text: use agent's greeting if available, otherwise use language-specific sample
  const getPreviewText = () => {
    // If agent has a custom greeting/welcome message, use that
    if (agent.welcomeMessage && agent.welcomeMessage.trim()) {
      return agent.welcomeMessage;
    }
    if (agent.greeting && agent.greeting.trim()) {
      return agent.greeting;
    }
    
    // Otherwise, use language-specific sample
    const primaryLanguage = agent.languages?.[0] || 'English';
    return sampleTexts[primaryLanguage] || sampleTexts['English'];
  };

  const sampleText = getPreviewText();

  // Play voice preview
  const playVoicePreview = async (voice, provider) => {
    try {
      setLoadingVoiceId(voice.id);
      setAudioError(null);
      
      // Completely stop and clean up any currently playing audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current = null;
      }
      if (audioElementRef.current) {
        audioElementRef.current.pause();
        audioElementRef.current.currentTime = 0;
        audioElementRef.current.src = '';
        // Force reload the audio element
        audioElementRef.current.load();
      }
      
      // Revoke any existing blob URL to free memory and prevent caching
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      setPlayingVoiceId(null);
      setAudioUrl(null);

      // Add timestamp to prevent any caching
      const timestamp = Date.now();
      const voiceId = voice.id.replace('-multilingual', '');
      
      console.log(`[${timestamp}] Playing voice preview: ${voice.name} (${provider}) - voice_id: ${voiceId}`);

      const response = await voiceAIAPI.preview(provider, voiceId, sampleText);
      
      const blob = response.data;
      console.log(`[${timestamp}] Received audio blob: ${blob.size} bytes, type: ${blob.type}, voice: ${voiceId}`);
      
      if (blob.size === 0) {
        setAudioError('Received empty audio');
        throw new Error('Empty audio response');
      }
      
      // Ensure the blob has the correct MIME type for MP3 audio
      // Some browsers require explicit MIME type for proper audio playback
      const audioBlob = blob.type === 'audio/mpeg' ? blob : new Blob([blob], { type: 'audio/mpeg' });
      console.log(`[${timestamp}] Audio blob type ensured: ${audioBlob.type}`);
      
      // Revoke previous URL if exists
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      
      // Create a unique blob URL
      const url = URL.createObjectURL(audioBlob);
      setAudioUrl(url);
      console.log(`[${timestamp}] Created audio URL for voice: ${voiceId}`);
      
      // IMPORTANT: Create a completely NEW Audio object each time
      // This ensures no caching or reuse of previous audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current = null;
      }
      
      const newAudio = new Audio();
      newAudio.volume = 1.0;
      newAudio.preload = 'auto';
      audioRef.current = newAudio;
      
      // Track if we've already started playing to prevent duplicate plays
      let hasStartedPlaying = false;
      
      // Set up event handlers BEFORE setting src
      newAudio.onended = () => {
        console.log(`[${timestamp}] Audio ended for voice: ${voiceId}`);
        setPlayingVoiceId(null);
        URL.revokeObjectURL(url);
        setAudioUrl(null);
      };
      
      newAudio.onerror = (e) => {
        const errorCode = newAudio.error ? newAudio.error.code : 'unknown';
        const errorMessage = newAudio.error ? newAudio.error.message : 'Unknown audio error';
        console.error(`[${timestamp}] Audio error for voice: ${voiceId}`, {
          errorCode,
          errorMessage,
          networkState: newAudio.networkState,
          readyState: newAudio.readyState,
          src: newAudio.src?.substring(0, 50)
        });
        // Only show error if we were actually trying to play (not just loading)
        if (loadingVoiceId === voice.id || playingVoiceId === voice.id) {
          setAudioError(`Audio playback error (${errorCode}): ${errorMessage}`);
        }
        setPlayingVoiceId(null);
      };
      
      // Wait for audio to be ready before playing - only play once
      newAudio.oncanplaythrough = async () => {
        if (hasStartedPlaying) return; // Prevent duplicate plays
        hasStartedPlaying = true;
        
        try {
          await newAudio.play();
          console.log(`[${timestamp}] Audio playback started for voice: ${voiceId}`);
          setPlayingVoiceId(voice.id);
          setAudioError(null); // Clear any previous errors
        } catch (playError) {
          console.error('Audio play() failed:', playError);
          setAudioError(`Playback error: ${playError.message}`);
          setPlayingVoiceId(null);
        }
      };
      
      // Set src after event handlers are attached
      newAudio.src = url;
      newAudio.load();
      
    } catch (error) {
      console.error('Error playing voice preview:', error);
      const errMsg = error.response?.data?.detail || error.message || 'Voice preview failed';
      setAudioError(errMsg);
      setPlayingVoiceId(null);
    } finally {
      setLoadingVoiceId(null);
    }
  };

  // Stop voice preview
  const stopVoicePreview = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }
    if (audioElementRef.current) {
      audioElementRef.current.pause();
      audioElementRef.current.src = '';
    }
    setPlayingVoiceId(null);
    setAudioUrl(null);
  };

  // Handle audio element events
  const handleAudioEnded = () => {
    setPlayingVoiceId(null);
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
  };

  const handleAudioError = (e) => {
    // Only handle error if we're actually trying to play audio (loadingVoiceId or playingVoiceId is set)
    // This ignores the initial error when audio element has empty src
    if (loadingVoiceId || playingVoiceId) {
      const audioEl = audioElementRef.current;
      // Check if there's a real error (not just empty src)
      if (audioEl && audioEl.error && audioEl.error.code !== 4) {
        // Code 4 is MEDIA_ERR_SRC_NOT_SUPPORTED which fires for empty src
        console.error('Audio element error:', e, audioEl.error);
        setAudioError('Audio playback failed');
        setPlayingVoiceId(null);
      }
    }
  };

  return (
    <div className="space-y-8">
      {/* Audio error display */}
      {audioError && (
        <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 text-red-400 text-sm">
          Audio Error: {audioError}
        </div>
      )}
      
      {/* Waveform Audio Player - Shows when playing voice preview */}
      {audioUrl && playingVoiceId && (
        <div className="bg-gradient-to-r from-teal-500/20 to-cyan-500/20 border border-teal-500/50 rounded-xl p-4 flex items-center gap-4 animate-in fade-in duration-300">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-teal-500/30 flex items-center justify-center">
              <Volume2 className="w-5 h-5 text-teal-400 animate-pulse" />
            </div>
            <div>
              <span className="text-teal-400 text-sm font-medium block">Playing Voice Preview</span>
              <span className="text-gray-500 text-xs">
                {voiceOptions[agent.voiceProvider]?.find(v => v.id === playingVoiceId)?.name || 'Unknown'}
              </span>
            </div>
          </div>
          
          {/* Waveform Visualizer */}
          <div className="flex-1 flex justify-center">
            <AudioWaveform 
              isPlaying={!!playingVoiceId} 
              audioElement={audioRef.current}
            />
          </div>
          
          <button 
            onClick={stopVoicePreview}
            className="px-4 py-2 bg-red-500/80 hover:bg-red-500 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
          >
            <Square className="w-3 h-3" />
            Stop
          </button>
        </div>
      )}
      
      {/* Assistant Settings */}
      <section className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
        <div className="flex items-center gap-2 mb-6">
          <Settings className="w-5 h-5 text-teal-400" />
          <h2 className="text-lg font-semibold text-white">Assistant Settings</h2>
          <button className="ml-auto text-gray-500 hover:text-gray-300">
            <Info className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Languages - Multi-select */}
          <div className="bg-[#1a1a1f] border border-gray-700 rounded-lg p-4 hover:border-gray-600 transition-colors col-span-2">
            <div className="flex items-center gap-2 mb-3">
              <Globe className="w-5 h-5 text-blue-400" />
              <span className="text-sm text-gray-400">Languages</span>
              <span className="text-xs text-teal-400 ml-auto">
                {agent.languages?.length || 0} selected
              </span>
            </div>
            <p className="text-xs text-gray-500 mb-3">Select all languages the agent should speak</p>
            <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto pr-1 custom-scrollbar">
              {['English', 'Russian', 'Uzbek', 'Spanish', 'French', 'German', 'Arabic', 'Chinese', 'Hindi', 'Portuguese', 'Japanese', 'Korean'].map(lang => (
                <label
                  key={lang}
                  className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                    agent.languages?.includes(lang)
                      ? 'bg-teal-500/20 border border-teal-500/50'
                      : 'bg-[#0d1117] border border-gray-700 hover:border-gray-600'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={agent.languages?.includes(lang) || false}
                    onChange={(e) => {
                      const newLanguages = e.target.checked
                        ? [...(agent.languages || []), lang]
                        : (agent.languages || []).filter(l => l !== lang);
                      setAgent(prev => ({ ...prev, languages: newLanguages }));
                    }}
                    className="w-4 h-4 rounded border-gray-600 text-teal-400 focus:ring-teal-400 focus:ring-offset-0 bg-gray-800"
                  />
                  <span className="text-white text-sm">{lang}</span>
                  {lang === 'Russian' && <span className="text-xs text-gray-500">🇷🇺</span>}
                  {lang === 'Uzbek' && <span className="text-xs text-gray-500">🇺🇿</span>}
                  {lang === 'English' && <span className="text-xs text-gray-500">🇺🇸</span>}
                </label>
              ))}
            </div>
          </div>

          {/* Voice (TTS) */}
          <div className="bg-[#1a1a1f] border border-gray-700 rounded-lg p-4 hover:border-gray-600 transition-colors col-span-2">
            <div className="flex items-center gap-2 mb-2">
              <Mic className="w-5 h-5 text-purple-400" />
              <span className="text-sm text-gray-400">Voice (TTS)</span>
              <Volume2 className="w-4 h-4 text-teal-400 ml-auto" title="Click play to preview voices" />
            </div>
            {hasFeature('voice_selection') ? (
              <>
                <select
                  value={agent.voiceProvider}
                  onChange={(e) => {
                    stopVoicePreview();
                    const newProvider = e.target.value;
                    const firstVoice = voiceOptions[newProvider]?.[0];
                    setAgent(prev => ({ 
                      ...prev, 
                      voiceProvider: newProvider,
                      voiceId: firstVoice?.id || '',
                      voiceGender: firstVoice?.gender || 'female'
                    }));
                  }}
                  className="w-full bg-transparent text-white text-sm outline-none mb-3"
                >
                  <option value="google">Google Cloud TTS</option>
                  <option value="elevenlabs">ElevenLabs</option>
                  <option value="openai">OpenAI TTS</option>
                  <option value="azure">Azure Speech</option>
                  <option value="playht">PlayHT</option>
                  <option value="deepgram">Deepgram Aura</option>
                </select>
                
                {/* Accent Filter */}
                <div className="mb-3">
                  <label className="text-xs text-gray-500 block mb-1">Filter by Accent</label>
                  <select
                    value={agent.voiceAccentFilter || 'all'}
                    onChange={(e) => {
                      setAgent(prev => ({
                        ...prev,
                        voiceAccentFilter: e.target.value
                      }));
                    }}
                    className="w-full bg-[#0d1117] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-teal-500"
                  >
                    <option value="all">All Accents</option>
                    <optgroup label="English Accents">
                      <option value="American">American</option>
                      <option value="British">British</option>
                      <option value="Australian">Australian</option>
                      <option value="Irish">Irish</option>
                      <option value="Southern American">Southern American</option>
                    </optgroup>
                    <optgroup label="European">
                      <option value="Swedish">Swedish</option>
                      <option value="Parisian French">French (Parisian)</option>
                      <option value="High German">German</option>
                      <option value="Standard Italian">Italian</option>
                      <option value="Castilian Spanish">Spanish (Castilian)</option>
                      <option value="European Portuguese">Portuguese (European)</option>
                    </optgroup>
                    <optgroup label="Russian">
                      <option value="Moscow Russian">Moscow Russian</option>
                      <option value="St. Petersburg">St. Petersburg</option>
                      <option value="Standard Russian">Standard Russian</option>
                    </optgroup>
                    <optgroup label="Uzbek">
                      <option value="Tashkent Uzbek">Tashkent Uzbek</option>
                      <option value="Samarkand Uzbek">Samarkand Uzbek</option>
                      <option value="Standard Uzbek">Standard Uzbek</option>
                    </optgroup>
                    <optgroup label="Asian">
                      <option value="Mandarin Chinese">Mandarin Chinese</option>
                      <option value="Standard Hindi">Hindi</option>
                    </optgroup>
                    <optgroup label="Arabic">
                      <option value="Modern Standard Arabic">Modern Standard Arabic</option>
                      <option value="Egyptian Arabic">Egyptian Arabic</option>
                      <option value="Gulf Arabic">Gulf Arabic</option>
                    </optgroup>
                  </select>
                </div>
                
                {/* Voice list with preview buttons */}
                <div className="max-h-48 overflow-y-auto space-y-1 pr-1 custom-scrollbar">
                  {voiceOptions[agent.voiceProvider]?.filter(voice => {
                    // Filter by allowed voices for OpenAI
                    if (agent.voiceProvider === 'openai') {
                      if (!isVoiceAllowed(voice.id)) return false;
                    }
                    // Filter by accent if selected
                    if (agent.voiceAccentFilter && agent.voiceAccentFilter !== 'all') {
                      if (voice.accent !== agent.voiceAccentFilter) return false;
                    }
                    return true;
                  })
                  .sort((a, b) => {
                    // Sort by selected languages first
                    const selectedLangs = agent.languages || ['English'];
                    const aLang = a.language || 'English';
                    const bLang = b.language || 'English';
                    const aSelected = selectedLangs.includes(aLang);
                    const bSelected = selectedLangs.includes(bLang);
                    if (aSelected && !bSelected) return -1;
                    if (!aSelected && bSelected) return 1;
                    return 0;
                  })
                  .map(voice => (
                    <div
                      key={voice.id}
                      onClick={() => {
                        setAgent(prev => ({ 
                          ...prev, 
                          voiceId: voice.id,
                          voiceGender: voice.gender || 'female'
                        }));
                      }}
                      className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                        agent.voiceId === voice.id 
                          ? 'bg-teal-500/20 border border-teal-500/50' 
                          : 'bg-[#0d1117] border border-gray-700 hover:border-gray-600'
                      }`}
                    >
                      <VoicePreviewButton
                        voice={voice}
                        provider={agent.voiceProvider}
                        isPlaying={playingVoiceId === voice.id}
                        onPlay={playVoicePreview}
                        onStop={stopVoicePreview}
                        isLoading={loadingVoiceId === voice.id}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                          <span className="text-white text-sm truncate">{voice.name}</span>
                          {voice.language === 'Russian' && <span className="text-xs">🇷🇺</span>}
                          {voice.language === 'Uzbek' && <span className="text-xs">🇺🇿</span>}
                          {voice.language === 'Spanish' && <span className="text-xs">🇪🇸</span>}
                          {voice.language === 'French' && <span className="text-xs">🇫🇷</span>}
                          {voice.language === 'German' && <span className="text-xs">🇩🇪</span>}
                          {voice.language === 'Arabic' && <span className="text-xs">🇸🇦</span>}
                          {voice.language === 'Hindi' && <span className="text-xs">🇮🇳</span>}
                          {voice.language === 'Chinese' && <span className="text-xs">🇨🇳</span>}
                          {voice.language === 'Portuguese' && <span className="text-xs">🇧🇷</span>}
                          {voice.language === 'Italian' && <span className="text-xs">🇮🇹</span>}
                        </div>
                        <span className="text-gray-500 text-xs">
                          {voice.gender} • {voice.accent}
                          {voice.description && ` • ${voice.description}`}
                        </span>
                      </div>
                      {agent.voiceId === voice.id && (
                        <div className="w-2 h-2 rounded-full bg-teal-400"></div>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Pitch Control */}
                {getVoiceSetting('pitch_adjustment_enabled') && (
                  <div className="mt-3 pt-3 border-t border-gray-700">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-gray-500">Pitch</span>
                      <span className="text-xs text-teal-400">{agent.voicePitch?.toFixed(1) || '1.0'}x</span>
                    </div>
                    <input
                      type="range"
                      min={getVoiceSetting('pitch_range')?.min || 0.5}
                      max={getVoiceSetting('pitch_range')?.max || 2.0}
                      step="0.1"
                      value={agent.voicePitch || 1.0}
                      onChange={(e) => setAgent(prev => ({ ...prev, voicePitch: parseFloat(e.target.value) }))}
                      className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-teal-400"
                    />
                  </div>
                )}
                
                {/* Speed Control */}
                {getVoiceSetting('speed_adjustment_enabled') && (
                  <div className="mt-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-gray-500">Speed</span>
                      <span className="text-xs text-teal-400">{agent.voiceSpeed?.toFixed(1) || '1.0'}x</span>
                    </div>
                    <input
                      type="range"
                      min={getVoiceSetting('speed_range')?.min || 0.5}
                      max={getVoiceSetting('speed_range')?.max || 2.0}
                      step="0.1"
                      value={agent.voiceSpeed || 1.0}
                      onChange={(e) => setAgent(prev => ({ ...prev, voiceSpeed: parseFloat(e.target.value) }))}
                      className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-teal-400"
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="text-gray-500 text-sm">
                <span className="block">Voice: {getVoiceSetting('default_voice') || 'nova'}</span>
                <span className="block text-xs mt-1 text-yellow-500">Voice selection disabled by admin</span>
              </div>
            )}
          </div>

          {/* AI Model (LLM) */}
          <div className="bg-[#1a1a1f] border border-gray-700 rounded-lg p-4 hover:border-gray-600 transition-colors">
            <div className="flex items-center gap-2 mb-2">
              <Bot className="w-5 h-5 text-green-400" />
              <span className="text-sm text-gray-400">AI Model (LLM)</span>
              <Info className="w-3 h-3 text-gray-600 ml-auto" />
            </div>
            <select
              value={agent.llmModel}
              onChange={(e) => {
                const model = llmModels.find(m => m.id === e.target.value);
                setAgent(prev => ({ 
                  ...prev, 
                  llmModel: e.target.value,
                  costPerMin: model?.costPerMin || 0.105
                }));
              }}
              className="w-full bg-transparent text-white text-sm outline-none"
            >
              <optgroup label="OpenAI">
                {openaiModels.map(model => (
                  <option key={model.id} value={model.id}>{model.name} - ${model.costPerMin}/min</option>
                ))}
              </optgroup>
              <optgroup label="Anthropic">
                {anthropicModels.map(model => (
                  <option key={model.id} value={model.id}>{model.name} - ${model.costPerMin}/min</option>
                ))}
              </optgroup>
              <optgroup label="Google">
                {googleModels.map(model => (
                  <option key={model.id} value={model.id}>{model.name} - ${model.costPerMin}/min</option>
                ))}
              </optgroup>
              <optgroup label="Meta (via Groq)">
                {metaModels.map(model => (
                  <option key={model.id} value={model.id}>{model.name} - ${model.costPerMin}/min</option>
                ))}
              </optgroup>
              <optgroup label="Mistral">
                {mistralModels.map(model => (
                  <option key={model.id} value={model.id}>{model.name} - ${model.costPerMin}/min</option>
                ))}
              </optgroup>
            </select>
            {selectedLLM && (
              <p className="text-gray-500 text-xs mt-2">{selectedLLM.description}</p>
            )}
          </div>

          {/* Transcription (STT) */}
          <div className="bg-[#1a1a1f] border border-gray-700 rounded-lg p-4 hover:border-gray-600 transition-colors cursor-pointer">
            <div className="flex items-center gap-2 mb-2">
              <AudioLines className="w-5 h-5 text-orange-400" />
              <span className="text-sm text-gray-400">Transcription (STT)</span>
              <Info className="w-3 h-3 text-gray-600 ml-auto" />
            </div>
            <select
              value={agent.transcriptionService}
              onChange={(e) => setAgent(prev => ({ ...prev, transcriptionService: e.target.value }))}
              className="w-full bg-transparent text-white text-sm outline-none"
            >
              {transcriptionServices.map(service => (
                <option key={service.id} value={service.id}>{service.name}</option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {/* Welcome Message */}
      <section className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
        <div className="flex items-center gap-2 mb-6">
          <MessageCircle className="w-5 h-5 text-teal-400" />
          <h2 className="text-lg font-semibold text-white">Welcome Message</h2>
          <button className="ml-auto text-gray-500 hover:text-gray-300">
            <Info className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-4">
          <ScriptEnhancer
            value={agent.welcomeMessage}
            onChange={(value) => setAgent(prev => ({ ...prev, welcomeMessage: value }))}
            fieldType="greeting"
            placeholder="Hi, I am a virtual assistant, and I'm calling from..."
            maxLength={600}
            language={agent.languages[0] || 'English'}
            showTemplates={true}
          />

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <div 
                className={`w-10 h-6 rounded-full p-1 transition-colors ${agent.welcomeMessageDynamic ? 'bg-teal-400' : 'bg-gray-700'}`}
                onClick={() => setAgent(prev => ({ ...prev, welcomeMessageDynamic: !prev.welcomeMessageDynamic }))}
              >
                <div className={`w-4 h-4 rounded-full bg-white transition-transform ${agent.welcomeMessageDynamic ? 'translate-x-4' : ''}`} />
              </div>
              <span className="text-sm text-gray-300">Dynamic</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <div 
                className={`w-10 h-6 rounded-full p-1 transition-colors ${agent.welcomeMessageInterruptible ? 'bg-teal-400' : 'bg-gray-700'}`}
                onClick={() => setAgent(prev => ({ ...prev, welcomeMessageInterruptible: !prev.welcomeMessageInterruptible }))}
              >
                <div className={`w-4 h-4 rounded-full bg-white transition-transform ${agent.welcomeMessageInterruptible ? 'translate-x-4' : ''}`} />
              </div>
              <span className="text-sm text-gray-300">Interruptible</span>
            </label>
          </div>
        </div>
      </section>

      {/* Conversational Flow */}
      <section className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
        <div className="flex items-center gap-2 mb-6">
          <FileText className="w-5 h-5 text-teal-400" />
          <h2 className="text-lg font-semibold text-white">Conversational Flow</h2>
          <button className="ml-auto text-gray-500 hover:text-gray-300">
            <Info className="w-4 h-4" />
          </button>
          <button
            onClick={addFlowSection}
            className="flex items-center gap-1 px-3 py-1.5 bg-teal-400/10 text-teal-400 rounded-lg text-sm hover:bg-teal-400/20 transition-colors"
            data-testid="add-section-btn"
          >
            <Plus className="w-4 h-4" />
            Add Section
          </button>
        </div>

        <div className="space-y-3">
          {agent.conversationalFlow.map((section, index) => (
            <div 
              key={section.id}
              className="bg-[#1a1a1f] border border-gray-700 rounded-lg overflow-hidden"
            >
              <div className="flex items-center gap-3 p-4">
                <GripVertical className="w-5 h-5 text-gray-600 cursor-grab" />
                <span className="text-gray-500 text-sm font-medium w-6">{index + 1}</span>
                <input
                  type="text"
                  value={section.title}
                  onChange={(e) => updateFlowSection(section.id, { title: e.target.value })}
                  className="flex-1 bg-transparent text-white outline-none"
                  placeholder="Section title..."
                />
                
                <div 
                  className={`px-3 py-1 rounded-full text-xs font-medium cursor-pointer ${section.enabled ? 'bg-green-400/10 text-green-400' : 'bg-gray-600/20 text-gray-500'}`}
                  onClick={() => updateFlowSection(section.id, { enabled: !section.enabled })}
                >
                  {section.enabled ? 'ON' : 'OFF'}
                </div>
                
                <button
                  onClick={() => deleteFlowSection(section.id)}
                  className="p-1.5 text-gray-500 hover:text-red-400 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              
              {section.enabled && (
                <div className="border-t border-gray-700 p-4">
                  <ScriptEnhancer
                    value={section.content}
                    onChange={(value) => updateFlowSection(section.id, { content: value })}
                    fieldType="section"
                    placeholder="Enter the script or instructions for this section..."
                    minHeight="100px"
                    maxLength={2000}
                    language={agent.languages[0] || 'English'}
                    showTemplates={false}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default DetailsTab;
